import { View } from 'foundations-components/components/View';
import { useGetQueryResultQuery, useSubmitQueryQuery } from '../../../../__generated__/chirp/com/hubspot/academy/ai/rpc/AcademyAIRpc.hooks';
import ApiError from 'academy-common-lib/components/error/ApiError';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { AIFullPageLoader } from 'ai-components-ui-library/components/loading/AIFullPageLoader';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { SkeletonBlock, SkeletonWrapper } from '../../../components/Skeletons/subcomponents/Skeleton';
import { Box } from 'foundations-components/transitional/components/Box';
import InnerContentDisplayCard from './InnerContentDisplayCard';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import UsageTracker from '../../../utils/UsageTracker';
import { Small } from 'foundations-components/transitional/components/Small';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const POLLING_TIMEOUT_MS = 30000;
const SkeletonText = () => {
  return /*#__PURE__*/_jsxs(SkeletonWrapper, {
    children: [/*#__PURE__*/_jsx(SkeletonBlock, {
      height: "14px",
      marginBlockEnd: "200",
      width: "90%"
    }), /*#__PURE__*/_jsx(SkeletonBlock, {
      height: "14px",
      marginBlockEnd: "200",
      width: "85%"
    }), /*#__PURE__*/_jsx(SkeletonBlock, {
      height: "14px",
      marginBlockEnd: "200",
      width: "82%"
    }), /*#__PURE__*/_jsx(SkeletonBlock, {
      height: "14px",
      marginBlockEnd: "200",
      width: "88%"
    })]
  });
};
const AISearchQueryAnswer = ({
  query
}) => {
  const pollingTimeoutRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    data: submitQueryData,
    loading: submitQueryLoading,
    error: submitQueryError
  } = useSubmitQueryQuery({
    variables: {
      query: query || ''
    },
    skip: !query
  });
  const {
    isTablet,
    isMobile
  } = useMediaQuery();
  useEffect(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'ai-search-query-submitted',
      search: query || ''
    });
  }, [query]);
  const {
    data: getQueryResultData,
    loading: getQueryResultLoading,
    error: getQueryResultError,
    startPolling,
    stopPolling
  } = useGetQueryResultQuery({
    variables: {
      referenceId: (submitQueryData === null || submitQueryData === void 0 ? void 0 : submitQueryData.referenceId) || ''
    },
    skip: !(submitQueryData !== null && submitQueryData !== void 0 && submitQueryData.referenceId)
  });
  const loading = submitQueryLoading || getQueryResultLoading;
  const error = submitQueryError || getQueryResultError;
  const data = getQueryResultData;
  useEffect(() => {
    let pollingTimeout;
    if (submitQueryData !== null && submitQueryData !== void 0 && submitQueryData.referenceId) {
      setIsExpanded(false);
      startPolling(500);
      pollingTimeout = setTimeout(() => {
        stopPolling();
        Metrics.counter('ai-search-query-polling-timeout').increment();
      }, POLLING_TIMEOUT_MS);
      pollingTimeoutRef.current = pollingTimeout;
    }
    return () => {
      stopPolling();
      clearTimeout(pollingTimeout);
    };
  }, [submitQueryData === null || submitQueryData === void 0 ? void 0 : submitQueryData.referenceId, startPolling, stopPolling]);
  useEffect(() => {
    if (['UNKNOWN', 'FAILURE', 'SUCCESS'].includes(readUnhandledEnumCase((data === null || data === void 0 ? void 0 : data.status) || ''))) {
      stopPolling();
      clearTimeout(pollingTimeoutRef.current || 0);
    }
  }, [data === null || data === void 0 ? void 0 : data.status, stopPolling]);
  const content = useMemo(() => {
    if (loading || (data === null || data === void 0 ? void 0 : data.status) === 'IN_PROGRESS' && (!(data !== null && data !== void 0 && data.suggestedContent) || !(data !== null && data !== void 0 && data.answer))) {
      return /*#__PURE__*/_jsx(CardSection, {
        blockSize: "100%",
        children: /*#__PURE__*/_jsx(AIFullPageLoader, {
          messages: [I18n.text('learningCenter.aiSearch.loading.breezeUsesAi'), I18n.text('learningCenter.aiSearch.loading.selectingContent'), I18n.text('learningCenter.aiSearch.loading.generatingAnswer'), I18n.text('learningCenter.aiSearch.loading.almostThere')]
        })
      });
    }
    if (error) {
      return /*#__PURE__*/_jsx(CardSection, {
        blockSize: "100%",
        children: /*#__PURE__*/_jsx(ApiError, {
          error: error
        })
      });
    }
    const {
      suggestedContent,
      answer,
      status,
      referenceId
    } = data || {};
    return /*#__PURE__*/_jsxs(CardSection, {
      blockSize: "100%",
      paddingBlock: "400",
      children: [/*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Text, {
            display: "inline-block",
            marginBlockEnd: "400",
            textTransform: "uppercase",
            typography: "heading-25",
            use: "help",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.aiSearch.title"
            })
          })
        }), /*#__PURE__*/_jsx(AIBadge, {})]
      }), suggestedContent || answer ? /*#__PURE__*/_jsxs(Flex, {
        flexDirection: isTablet ? 'column-reverse' : 'row',
        gap: "800",
        children: [/*#__PURE__*/_jsx(Box, {
          grow: 1,
          children: status === 'SUCCESS' ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ExpandableText, {
              collapseButtonText: null,
              expanded: isExpanded,
              hideButtonCaret: isExpanded,
              maxHeight: 115,
              onExpandedChange: expanded => {
                setIsExpanded(expanded);
                UsageTracker.trackStandalone('Learning Center Interaction', {
                  action: 'ai-search-query-answer-expanded',
                  search: query || '',
                  answer: answer || ''
                });
              },
              children: answer
            }), /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(Box, {
                marginInlineEnd: "400",
                children: /*#__PURE__*/_jsx(InlineFeedback, {
                  messageId: referenceId || '',
                  onNegativeFeedback: () => {
                    UsageTracker.trackStandalone('Learning Center Interaction', {
                      action: 'ai-search-query-answer-negative-feedback',
                      search: query || '',
                      answer: answer || ''
                    });
                  },
                  onPositiveFeedback: () => {
                    UsageTracker.trackStandalone('Learning Center Interaction', {
                      action: 'ai-search-query-answer-positive-feedback',
                      search: query || '',
                      answer: answer || '',
                      contentId: (suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.contentId) || 0
                    });
                  },
                  usageTracker: UsageTracker.tracker
                })
              }), /*#__PURE__*/_jsx(Small, {
                marginInlineEnd: "200",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.aiSearch.aiWarning"
                })
              }), /*#__PURE__*/_jsx(HelpIcon, {
                title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                  message: "learningCenter.aiSearch.aiWarningTooltip"
                })
              })]
            })]
          }) : status === 'IN_PROGRESS' ? /*#__PURE__*/_jsx(SkeletonText, {}) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.aiSearch.error"
          })
        }), /*#__PURE__*/_jsx(Box, {
          inlineSize: isTablet ? '100%' : '360px',
          minInlineSize: isTablet ? '100%' : '360px',
          children: /*#__PURE__*/_jsx(InnerContentDisplayCard, {
            containerId: (suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.contentContainerId) || 0,
            containerType: readUnhandledEnumCase((suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.contentContainerType) || ''),
            innerContentId: (suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.contentId) || 0,
            onClick: () => {
              UsageTracker.trackStandalone('Learning Center Interaction', {
                action: 'ai-search-query-answer-suggested-content-card-click',
                search: query || '',
                answer: answer || '',
                contentId: (suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.contentId) || 0
              });
            },
            relevantTimestamp: suggestedContent === null || suggestedContent === void 0 ? void 0 : suggestedContent.startTime
          })
        })]
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.aiSearch.fallback"
      })]
    });
  }, [data, loading, error, isExpanded, isTablet, query]);
  if (!query) {
    return null;
  }
  return /*#__PURE__*/_jsx(CardWrapper, {
    compact: true,
    marginBlockEnd: isMobile ? '800' : undefined,
    minBlockSize: isTablet ? '360px' : '220px',
    children: content
  });
};
export default AISearchQueryAnswer;